import {deprecatedTrackEvent} from 'analytics/Analytics';
import {assertAndContinue} from 'Asserts';
import {INSIGHTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {refreshDetails} from 'insights/analytics-dashboards/details/AnalyticsDashboardDetailsActions';
import getParameterNamesInSQLQueries from 'insights/parameters/data/getParameterNamesInSQLQueries';
import {refreshListingsWithAnalysesAndAnalyticsDashboards} from 'insights/sidepanel/AnalyticsSearchActions';
import navigateToURL from 'lib/navigateToURL';

import * as AnalyticsDashboardDAO from './AnalyticsDashboardDAO';
import {
  convertRESTToGQLAnalyticsDashboard,
  GraphQLAnalyticsDashboardSpecFragment,
} from './data/AnalyticsDashboardGQLRESTConverters';
import {
  AnalyticsDashboardSpecRequest,
  AnalyticsDashboardSpecResponse,
  UpdateAnalyticsDashboardSpecRequest,
} from './RESTAnalyticsDashboardTypes';

function checkSpecRequestParameterNames(
  analyticsDashboardId: string,
  data: UpdateAnalyticsDashboardSpecRequest
): void {
  const parameterNamesInSQLQuery = getParameterNamesInSQLQueries(
    data.analyticsQueries.map((analyticsQuery) => analyticsQuery.sqlQuery)
  );
  const parameterNamesInConfigs = data.parameterConfigs.map((parameterConfig) => parameterConfig.systemName);
  assertAndContinue(
    parameterNamesInSQLQuery.length === parameterNamesInConfigs.length &&
      parameterNamesInSQLQuery.every((name) => parameterNamesInConfigs.includes(name)),
    "Expected parameter names in dashboard's request to be equal in parameter configs and sql query contents(BNCH-12266)",
    {analyticsDashboardId, parameterNamesInConfigs, parameterNamesInSQLQuery}
  );
}

function writeToCache(restAnalyticsDashboard: AnalyticsDashboardSpecResponse): void {
  const gqlAnalyticsDashboard = convertRESTToGQLAnalyticsDashboard(restAnalyticsDashboard);
  writeFragmentsToCache(GraphQLAnalyticsDashboardSpecFragment, [gqlAnalyticsDashboard]);
}

export function handleDashboardCreated(restAnalyticsDashboard: AnalyticsDashboardSpecResponse): void {
  writeToCache(restAnalyticsDashboard);
  refreshListingsWithAnalysesAndAnalyticsDashboards();
  navigateToURL(restAnalyticsDashboard.url);
  deprecatedTrackEvent(INSIGHTS_ANALYTICS_ACTIONS.CREATED_DASHBOARD, {
    dashboard_id: restAnalyticsDashboard.id,
    project_id: restAnalyticsDashboard.projectId,
  });
}

export async function createAnalyticsDashboard(
  data: AnalyticsDashboardSpecRequest
): Promise<AnalyticsDashboardSpecResponse> {
  const restAnalyticsDashboard = await AnalyticsDashboardDAO.createAnalyticsDashboard(data);
  handleDashboardCreated(restAnalyticsDashboard);
  return restAnalyticsDashboard;
}

export async function updateAnalyticsDashboard(
  analyticsDashboardId: string,
  data: UpdateAnalyticsDashboardSpecRequest
): Promise<AnalyticsDashboardSpecResponse> {
  checkSpecRequestParameterNames(analyticsDashboardId, data);
  const restAnalyticsDashboard = await AnalyticsDashboardDAO.updateAnalyticsDashboard(
    analyticsDashboardId,
    data
  );
  writeToCache(restAnalyticsDashboard);
  await refreshDetails(analyticsDashboardId);
  return restAnalyticsDashboard;
}

export async function deleteAnalyticsDashboard(analyticsDashboardId: string): Promise<void> {
  await AnalyticsDashboardDAO.deleteAnalyticsDashboard(analyticsDashboardId);
  refreshListingsWithAnalysesAndAnalyticsDashboards();
}

export async function cloneAnalyticsDashboard(
  analyticsDashboardId: string,
  data: {projectId: string}
): Promise<AnalyticsDashboardSpecResponse> {
  const restAnalyticsDashboard = await AnalyticsDashboardDAO.cloneAnalyticsDashboard(
    analyticsDashboardId,
    data
  );
  refreshListingsWithAnalysesAndAnalyticsDashboards();
  return restAnalyticsDashboard;
}

export async function starAnalyticsDashboards(analyticsDashboardIds: Array<string>): Promise<void> {
  await AnalyticsDashboardDAO.starAnalyticsDashboards(analyticsDashboardIds);
  refreshListingsWithAnalysesAndAnalyticsDashboards();
}

export async function unstarAnalyticsDashboards(analyticsDashboardIds: Array<string>): Promise<void> {
  await AnalyticsDashboardDAO.unstarAnalyticsDashboards(analyticsDashboardIds);
  refreshListingsWithAnalysesAndAnalyticsDashboards();
}

export interface AnalyticsDashboardActions {
  cloneAnalyticsDashboard: typeof cloneAnalyticsDashboard;
  createAnalyticsDashboard: typeof createAnalyticsDashboard;
  deleteAnalyticsDashboard: typeof deleteAnalyticsDashboard;
  starDashboards: typeof starAnalyticsDashboards;
  unstarDashboards: typeof unstarAnalyticsDashboards;
  updateAnalyticsDashboard: typeof updateAnalyticsDashboard;
}
